import { addWeeks, isAfter } from 'date-fns'

import { EMPTY_LIST } from '../../../constants/common'
import {
  DATE_DAY_MONTH_SHORT_FORMAT,
  DATE_HOURS_FORMAT,
} from '../../../constants/date'
import { RENTAL_PORTAL_FACT_SEPARATOR } from '../../../constants/rentalPortal'
import { formatDate } from '../../Table'
import { IS_VIEWING_BOOKING_AVAILABLE } from '../RentalPortalViewing.constants'

import {
  VIEWING_FORM_DURATION_DEFAULT,
  VIEWING_FORM_DURATION_OPTIONS,
  VIEWING_FORM_FIELD_NAME,
  VIEWING_FORM_MODE,
  VIEWING_FORM_MODE_LABEL_KEY,
  VIEWING_FORM_TIME_SOURCE,
  VIEWING_FORM_TIME_SOURCE_LABEL_KEY,
} from './RentalPortalViewingFormModal.constants'

function getHasViewingFormApplicants(values) {
  return values[VIEWING_FORM_FIELD_NAME.applicant].length > 0
}

function getViewingFormDurationOption({ minutes, t }) {
  return {
    label: t('RentalPortal.ViewingFormDurationMinutesKey', { count: minutes }),
    value: minutes,
  }
}

export function getIsViewingFormSlotTimeSource(values) {
  return (
    values[VIEWING_FORM_FIELD_NAME.timeSource] === VIEWING_FORM_TIME_SOURCE.slot
  )
}

export function getIsViewingFormSlotSource(values) {
  return (
    getIsViewingFormBookMode(values) && getIsViewingFormSlotTimeSource(values)
  )
}

export function getViewingFormTimeSourceOptions(t) {
  return Object.values(VIEWING_FORM_TIME_SOURCE).map(source => ({
    id: source,
    label: t(VIEWING_FORM_TIME_SOURCE_LABEL_KEY[source]),
  }))
}

export function getIsViewingFormBookMode(values) {
  return values[VIEWING_FORM_FIELD_NAME.mode] === VIEWING_FORM_MODE.book
}

export function getViewingFormInitialValues({
  applicant,
  isBooking,
  listing,
  locale,
  slot,
  start,
  t,
  timezone,
}) {
  return {
    [VIEWING_FORM_FIELD_NAME.applicant]: applicant ? [applicant] : EMPTY_LIST,
    [VIEWING_FORM_FIELD_NAME.duration]: getViewingFormDurationOption({
      minutes: slot?.duration_min ?? VIEWING_FORM_DURATION_DEFAULT,
      t,
    }),
    [VIEWING_FORM_FIELD_NAME.listing]:
      listing ??
      (slot ? { label: slot.listing_label, value: slot.listing_id } : null),
    [VIEWING_FORM_FIELD_NAME.mode]:
      IS_VIEWING_BOOKING_AVAILABLE && (isBooking || slot || applicant)
        ? VIEWING_FORM_MODE.book
        : VIEWING_FORM_MODE.addSlot,
    [VIEWING_FORM_FIELD_NAME.repeatUntil]: null,
    [VIEWING_FORM_FIELD_NAME.slot]: slot
      ? toViewingFormSlotItems({ items: [slot], locale, timezone })[0]
      : null,
    [VIEWING_FORM_FIELD_NAME.start]: slot
      ? new Date(slot.start)
      : (start ?? null),
    [VIEWING_FORM_FIELD_NAME.timeSource]:
      slot || isBooking
        ? VIEWING_FORM_TIME_SOURCE.slot
        : VIEWING_FORM_TIME_SOURCE.adHoc,
    [VIEWING_FORM_FIELD_NAME.withRepeat]: false,
  }
}

export function getViewingFormModeOptions(t) {
  return [
    {
      id: VIEWING_FORM_MODE.book,
      label: t(VIEWING_FORM_MODE_LABEL_KEY[VIEWING_FORM_MODE.book]),
    },
    {
      id: VIEWING_FORM_MODE.addSlot,
      label: t(VIEWING_FORM_MODE_LABEL_KEY[VIEWING_FORM_MODE.addSlot]),
    },
  ]
}

export function toViewingFormListingItems(items) {
  return items.map(listing => ({
    label: listing.unit_label,
    value: listing.id,
  }))
}

export function toViewingFormDurationOptions(t) {
  return VIEWING_FORM_DURATION_OPTIONS.map(minutes => ({
    label: t('RentalPortal.ViewingFormDurationMinutesKey', { count: minutes }),
    value: minutes,
  }))
}

export function getViewingFormSeriesStarts({
  [VIEWING_FORM_FIELD_NAME.repeatUntil]: repeatUntil,
  [VIEWING_FORM_FIELD_NAME.start]: start,
  [VIEWING_FORM_FIELD_NAME.withRepeat]: withRepeat,
}) {
  if (!start) {
    return EMPTY_LIST
  }

  if (!withRepeat || !repeatUntil) {
    return [start]
  }

  const starts = []

  let current = start

  while (!isAfter(current, repeatUntil)) {
    starts.push(current)

    current = addWeeks(current, 1)
  }

  return starts
}

export function getIsViewingFormSubmittable(values) {
  if (getIsViewingFormSlotSource(values)) {
    return Boolean(
      values[VIEWING_FORM_FIELD_NAME.listing] &&
      values[VIEWING_FORM_FIELD_NAME.slot] &&
      getHasViewingFormApplicants(values)
    )
  }

  return Boolean(
    values[VIEWING_FORM_FIELD_NAME.listing] &&
    values[VIEWING_FORM_FIELD_NAME.start] &&
    values[VIEWING_FORM_FIELD_NAME.duration] &&
    (!getIsViewingFormBookMode(values) || getHasViewingFormApplicants(values))
  )
}

export function getViewingFormSubmitTextKey(values) {
  return getIsViewingFormBookMode(values)
    ? 'RentalPortal.ViewingFormBookSubmitKey'
    : 'RentalPortal.ViewingFormAddSlotSubmitKey'
}

export function toViewingFormSlotItems({ items, locale, timezone }) {
  return items.map(slot => {
    const timeRange = `${formatDate({
      date: slot.start,
      dateFormat: DATE_HOURS_FORMAT,
      locale,
      timezone,
      useTodayFormat: false,
    })}–${formatDate({
      date: slot.end,
      dateFormat: DATE_HOURS_FORMAT,
      locale,
      timezone,
      useTodayFormat: false,
    })}`

    const dateLabel = formatDate({
      date: slot.start,
      dateFormat: DATE_DAY_MONTH_SHORT_FORMAT,
      locale,
      timezone,
      useTodayFormat: false,
    })

    return {
      ...slot,
      label: timeRange,
      value: slot.slot_id,
      valueLabel: `${dateLabel}${RENTAL_PORTAL_FACT_SEPARATOR}${timeRange}`,
    }
  })
}

export function toViewingFormApplicantItems(items) {
  return items.map(applicant => ({
    avatar: {
      color: applicant.avatar_color,
      stage: applicant.stage,
      text: applicant.identity.value,
    },
    label: applicant.identity.value,
    stage: applicant.stage,
    value: applicant.id,
  }))
}
